import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { type HaulRoute } from 'api/haul-route.api';
import { ROAD_CONDITIONS } from './roadsImporter.constants';
import { sanitizeIntegerInput } from './roadsImporter.helpers';

export interface EditRouteFormState {
  roadName: string;
  minutes: string;
  capacity: string;
  weightCapacity: string;
  speedLimitMin: string;
  speedLimitMax: string;
  lanes: string;
  condition: string;
  suggestions: string;
}

interface EditRouteDialogProps {
  target: HaulRoute | null;
  value: EditRouteFormState;
  onChange: (next: EditRouteFormState) => void;
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
}

export function EditRouteDialog({
  target,
  value,
  onChange,
  saving,
  onClose,
  onSave,
}: EditRouteDialogProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Dialog
      open={target !== null}
      onClose={() => {
        if (!saving) onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t('roads_importer_edit_route_title', { name: target?.routeKey ?? '' })}
      </DialogTitle>
      <DialogContent
        sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 1 }}
      >
        <TextField
          autoFocus
          size="small"
          label={t('roads_importer_field_road_name')}
          value={value.roadName}
          onChange={(e) => {
            onChange({ ...value, roadName: e.target.value });
          }}
          sx={{ gridColumn: '1 / -1', mt: 1 }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_minutes')}
          value={value.minutes}
          onChange={(e) => {
            onChange({
              ...value,
              minutes: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_capacity')}
          value={value.capacity}
          onChange={(e) => {
            onChange({
              ...value,
              capacity: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_weight_capacity')}
          value={value.weightCapacity}
          onChange={(e) => {
            onChange({
              ...value,
              weightCapacity: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_speed_limit_min')}
          value={value.speedLimitMin}
          onChange={(e) => {
            onChange({
              ...value,
              speedLimitMin: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_speed_limit_max')}
          value={value.speedLimitMax}
          onChange={(e) => {
            onChange({
              ...value,
              speedLimitMax: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_lanes')}
          value={value.lanes}
          onChange={(e) => {
            onChange({ ...value, lanes: sanitizeIntegerInput(e.target.value) });
          }}
        />
        <TextField
          select
          size="small"
          label={t('roads_importer_field_road_condition')}
          value={value.condition}
          onChange={(e) => {
            onChange({ ...value, condition: e.target.value });
          }}
        >
          {ROAD_CONDITIONS.map((condition) => (
            <MenuItem key={condition} value={condition}>
              {t(`roads_importer_condition_${condition}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          multiline
          minRows={3}
          label={t('roads_importer_field_geo_suggestions')}
          value={value.suggestions}
          onChange={(e) => {
            onChange({ ...value, suggestions: e.target.value });
          }}
          sx={{ gridColumn: '1 / -1' }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={saving}
          onClick={onClose}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_cancel')}
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={14} /> : <CheckIcon />}
          disabled={saving}
          onClick={onSave}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_save_changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
